import { SqlLineChart } from "./components/SqlLineChart";
import { ArticleViewsChart } from "./charts/ArticleViewsChart";
import { ArticleCommentsChart } from "./charts/ArticleCommentsChart";
import { CampaignTrafficChart } from "./charts/CampaignTrafficChart";
import { UserTagsChart } from "./charts/UserTagsChart";
import { ArticleVisitSubscriberChart } from "./charts/ArticleVisitSubscribeChart";
import { ArticleStartSubscribeChart } from "./charts/ArticleStartSubscribeChart";
import { SubscriberBreakdownChart } from "./charts/SubcriberBreakdownChart";
import { SqlDataTable } from "./components/SqlDataTable";
import { UserVisitsByView } from "./charts/UserVisitsByView";
import { UserVisitsByDay } from "./charts/UserVisitsByDay";
import { NotificationsChart } from "./charts/NotificationsChart";
import { ArticleNotificationsChart } from "./charts/ArticleNotificationsChart";
import { PinEntry } from "@/components/elements/PinEntry";

export function ArticleTrafficReport() {
  return <PinEntry>
    <div>
      <div className="flex flex-col p-4" style={{gap: 24, width: 1200}}>
        <b>7 Day Article Traffic Report</b>
        <SqlLineChart endTime={0} startTime={0} width={1200}>{`
          select
            time_bucket(INTERVAL '30m', epoch_ms(ts)) AS time,
            'views' as label,
            count(distinct session_id) as count
          from events 
          where epoch_ms(ts) > (now() - INTERVAL 7 DAY) and epoch_ms(ts) <= now()
          group by time
          order by time asc
        `}</SqlLineChart>

        <SqlDataTable label="User Visits by Day" subtext="Number of unique user visits per day. (Last 7 days)" width={600}
          formatData={(data) => {
            data.forEach(d => d.name = (d?.name || '').split('T')?.[0])
            return data;
          }}
        >{`
          select
            time_bucket(INTERVAL '1 day', epoch_ms(ts)) AS name,
            count(distinct session_id) as value
          from events 
          where epoch_ms(ts) > (now() - INTERVAL 7 DAY) and epoch_ms(ts) <= now()
          group by name
          order by name desc
        `}</SqlDataTable>

        <UserVisitsByDay/>
        <UserVisitsByView/>

        <SqlDataTable label="User Inactivity" subtext="Number of days since users last activity. (All Time)" width={600}>{`
            select
              label as name,
              sum(value) as value
            from (
              select DATEDIFF('day', last_active, now()) as age,
              CASE WHEN age <= 1 THEN 'Today' WHEN
                age > 1 and age <= 30 THEN 'Last 30 Days' WHEN 
                age > 30 and age <= 60 THEN 'Last 60 Days' else 'Last 60+ Days' END as label,
              count(*) as value,
              from users
              group by age
              order by age desc
            )
            group by label
        `}</SqlDataTable>

        <SqlDataTable label="Account lifetime" subtext="Number of days that accounts were active. (All Time)" width={600}>{`
            select
              label as name,
              sum(value) as value
            from (
              select DATEDIFF('day', created, last_active) as age,
              CASE WHEN age <= 1 THEN '1 Day' WHEN
                age > 1 and age <= 30 THEN '30 Days' WHEN 
                age > 30 and age <= 60 THEN '60 Days' else '60+ Days' END as label,
              count(*) as value,
              from users
              group by age
              order by age desc
            )
            group by label
        `}</SqlDataTable>     

        <ArticleViewsChart/>
        
        <ArticleCommentsChart/>

        <CampaignTrafficChart/>

        <SqlDataTable label="Active User Accounts" subtext="Users using a login vs guests. (Last 7 days)" width={400}>{`
          select count(distinct user_id) as value, 'Account' as name
          from events
          where user_id is not null
          and epoch_ms(ts) > (now() - INTERVAL 7 DAY)
          union
          select
          count(distinct session_id) as value, 'Guest' as name
          from events
          where user_id is null
          and http_agent_type = 'user'
          and epoch_ms(ts) > (now() - INTERVAL 7 DAY)
        `}</SqlDataTable>


        <div className="flex flex-row" style={{gap: 24}}>
          <SqlDataTable label="Tags by views" subtext="Most viewed tags (All Time)" width={600}>{`
            select 
              tags.scope as name,
              sum(articles.views) as value
            from tags
            inner join article_tags on article_tags.tag_id = tags.id
            inner join articles on articles.id = article_tags.article_id
            where articles.views > 0
            group by scope
            order by value desc
          `}</SqlDataTable>

          <SqlDataTable label="Tags by articles" subtext="Tags used by article. (All Time)" width={600}>{`
            select 
              tags.scope as name,
              count(*) as value
            from tags
            inner join article_tags on article_tags.tag_id = tags.id
            inner join articles on articles.id = article_tags.article_id
            where articles.views > 0
            group by scope
            order by value desc
          `}</SqlDataTable>

          <UserTagsChart/>

        </div>

        <SqlDataTable label="Tags by comments" subtext="Tags by comments. (All Time)" width={600}>{`
            select 
              tags.scope as name,
              sum(articles.comments) as value
            from tags
            inner join article_tags on article_tags.tag_id = tags.id
            inner join articles on articles.id = article_tags.article_id
            where articles.comments > 0
            group by scope
            order by value desc
        `}</SqlDataTable>

        <ArticleVisitSubscriberChart/>
        <ArticleStartSubscribeChart/>

        <SubscriberBreakdownChart/>

        <SqlDataTable label="Sessions by Platform" subtext="Sessions on Web, iOS and Android. (Last 7 days)" width={600}>{`
          select
            platform as name,
            count(distinct session_id) as value
          from events
          where epoch_ms(ts) > (now() - INTERVAL 7 DAY)
          group by platform
        `}</SqlDataTable>

        <SqlDataTable label="App load time" subtext="Time(in milliseconds) to first page. (Last 7 days)" width={600} extraColumns={appLoadTimeColumns}>{`
          select
            platform as name,
            count(1) as value,
            min((attr->'$.time')::INT) as min,
            max((attr->'$.time')::INT) as max,
            avg((attr->'$.time')::INT) as avg,
          from events 
          where label = 'ttfp' and epoch_ms(ts) > (now() - INTERVAL 7 DAY) and (attr->'$.time')::INT is not null
          group by platform
        `}</SqlDataTable>

        <NotificationsChart/>
        <ArticleNotificationsChart/>

        <SqlDataTable
          label="Paid vs Free Articles (Month)"
          subtext="MXM paid articles, free articles and total articles by week of year. (Year 2025)"
          width={600}
          extraColumns={paidVsFreeColumns}
          noDefaultColumn
        >{`
          select
            month(date) as month,
            sum(case when paywall = 'free' then 1 else 0 end) as free,
            sum(case when paywall = 'paid' then 1 else 0 end) as paid,
            count(1) as total
          from articles
            inner join article_tags on articles.id = article_tags.article_id
            where article_tags.tag_id = '7dad7fad-8858-4bc9-b069-52ab3cbe0e9c'
            and year(date) = 2025
            group by month
          order by month
        `}</SqlDataTable>
        
        <SqlDataTable
          label="Paid vs Free Articles (Week)"
          subtext="MXM paid articles, free articles and total articles by week of year. (Year 2025)"
          width={600}
          extraColumns={paidVsFreeColumnsWeek}
          noDefaultColumn
        >{`
          select
            week(date) as week,
            sum(case when paywall = 'free' then 1 else 0 end) as free,
            sum(case when paywall = 'paid' then 1 else 0 end) as paid,
            count(1) as total
          from articles
            inner join article_tags on articles.id = article_tags.article_id
            where article_tags.tag_id = '7dad7fad-8858-4bc9-b069-52ab3cbe0e9c'
            and year(date) = 2025
            group by week
          order by week
        `}</SqlDataTable>

      <SqlDataTable label="Subscriptions" subtext="Subscriptions by status and provider. Subscriptions will display as active until they have expired, even if there is a cancellation hint. (All Time)"
        width={600} noDefaultColumn extraColumns={subscriptionColumns}>{`
        select
          meta->>'$.billingProvider' as billingProvider,
          meta->>'$.status' as status,
          meta->>'$.statusHint' as statusHint,
          meta->>'$.productId' as productId,
          count(*) as count,
        from subscriptions
        group by billingProvider,status, statusHint, productId
        order by billingProvider,status, statusHint, productId

        `}</SqlDataTable>
        
        <SqlDataTable label="Subscription Retention" subtext="How many months subscriptions were active before being canceled, lapsed or inactive. (All Time)"
        width={600} noDefaultColumn extraColumns={subscriptionRetentionColumns}>{`
          select
          months,
          count(*) as count
          from (
          select DATEDIFF('month', start_ts, end_ts) as months from subscriptions where end_ts < now()
          ) data
          group by months
          order by months

        `}</SqlDataTable>
      </div>
    </div>
  </PinEntry>
}

const appLoadTimeColumns = [
  {field: 'min', label: "Min"},
  {field: 'max', label: "Max"},
  {field: 'avg', label: "Avg"},
];

const paidVsFreeColumns = [
  {field: 'month', label: "Month"},
  {field: 'free', label: "Free"},
  {field: 'paid', label: "Paid"},
  {field: 'total', label: "Total"},
];

const paidVsFreeColumnsWeek = [
  {field: 'week', label: "Week"},
  {field: 'free', label: "Free"},
  {field: 'paid', label: "Paid"},
  {field: 'total', label: "Total"},
]

const subscriptionColumns = [
  {field: 'billingProvider', label: "Billing Provider"},
  {field: 'status', label: "Status"},
  {field: 'statusHint', label: "Status Hint"},
  {field: 'productId', label: "Product ID or SKU"},
  {field: 'count', label: "Count"}
];

const subscriptionRetentionColumns = [
  {field: 'months', label: "Months"},
  {field: 'count', label: "Count"}
];